import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, Eye, Heart, Shield, CheckCircle, Globe, Users, Award } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">About G5 Petroleum</h1>
            <p className="text-xl text-blue-100">
              Engineering excellence in marine and industrial solutions across Southeast Asia
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg border-0">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl font-bold text-gray-900 mb-4">Company Overview</CardTitle>
              </CardHeader>
              <CardContent className="text-lg text-gray-700 leading-relaxed space-y-4">
                <p>
                  <strong>G5 Petroleum Sdn Bhd</strong> (PETRONAS Reg: 202201037944) is a Malaysia-based engineering and supply company headquartered in Ampang Jaya, Selangor, specializing in marine hose systems, flexible industrial hoses, pig launcher systems, and ship brokering services.
                </p>
                <p>
                  With strong partnerships across Asia and the Middle East, we deliver high-quality solutions to oil & gas terminals, industrial plants, offshore operators, and marine logistics companies.
                </p>
                <p>
                  Our commitment to engineering excellence and customer satisfaction has established us as a trusted partner in the marine and industrial sectors throughout Southeast Asia.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Vision */}
            <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-lg text-center">
                  To be a leading engineering solutions partner for marine and industrial operations across Southeast Asia.
                </CardDescription>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Deliver world-class products that meet global standards</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Provide reliable and responsive technical support</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Build long-term partnerships with clients and manufacturers</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Offer competitive pricing without compromising quality</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide our operations and relationships</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Integrity */}
            <Card className="text-center hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Integrity</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  We uphold transparency and professionalism in all our business dealings
                </CardDescription>
              </CardContent>
            </Card>

            {/* Reliability */}
            <Card className="text-center hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">Reliability</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  On-time supply, consistent support, and dependable service delivery
                </CardDescription>
              </CardContent>
            </Card>

            {/* Technical Excellence */}
            <Card className="text-center hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Technical Excellence</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Knowledge-based solutions backed by engineering expertise
                </CardDescription>
              </CardContent>
            </Card>

            {/* Safety First */}
            <Card className="text-center hover:shadow-lg transition-shadow border-0 shadow-md">
              <CardHeader>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-xl">Safety First</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Compliance with international standards and safety protocols
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Regional Reach */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Our Malaysia Coverage</h2>
            <p className="text-xl text-blue-100">Serving customers across all major states in Malaysia</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white">Serving customers across:</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                  {[
                    "Terengganu, Malaysia",
                    "Johor, Malaysia", 
                    "Sabah, Malaysia",
                    "Sarawak, Malaysia",
                    "Penang, Malaysia",
                    "Selangor, Malaysia"
                  ].map((location, index) => (
                    <Badge key={index} variant="secondary" className="bg-white/20 text-white border-white/30 py-2 px-4 text-base">
                      {location}
                    </Badge>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <p className="text-blue-100 text-lg">
                    Strategic partnerships and local presence ensure responsive service and competitive solutions across Malaysia.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
              <div className="text-xl font-semibold text-gray-900 mb-2">Years Experience</div>
              <div className="text-gray-600">Combined marine & industrial expertise</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500 mb-2">6</div>
              <div className="text-xl font-semibold text-gray-900 mb-2">Countries Served</div>
              <div className="text-gray-600">Across Southeast Asia and Middle East</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">100+</div>
              <div className="text-xl font-semibold text-gray-900 mb-2">Projects Completed</div>
              <div className="text-gray-600">Successful marine & industrial solutions</div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default About;